/**
 * Decision Brief — Shareable Read-Only View (Public, token-gated)
 *
 * Route: /brief/view/:token  —  No auth required.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { getBriefByViewToken } from '../../services/brief/briefService';
import type { BriefPublicView } from '../../types/brief';
import { DECISION_TYPE_LABELS, DECISION_TYPE_COLORS, DECISION_SCOPE_LABELS, BRIEF_RESOLUTION_LABELS } from '../../types/brief';

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-xs text-[var(--text-ter)] uppercase tracking-wide mb-0.5">{label}</dt>
      <dd className="text-sm text-[var(--text-sec)]">{value}</dd>
    </div>
  );
}

export default function BriefView() {
  const { token } = useParams<{ token: string }>();
  const [view, setView] = useState<BriefPublicView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await getBriefByViewToken(token!);
        if (!data) { setError('This link is invalid or the brief is not yet available.'); return; }
        setView(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load brief');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[var(--rule-bright)] border-t-[var(--text-sec)] rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !view) {
    return (
      <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center px-6">
        <div className="max-w-sm text-center">
          <h2 className="text-base font-medium text-[var(--text-pri)] mb-2">Brief not found</h2>
          <p className="text-sm text-[var(--text-sec)]">{error ?? 'This link may be invalid or the brief has been removed.'}</p>
        </div>
      </div>
    );
  }

  const fmt = (d?: string) => d
    ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  const selectedIds = new Set(view.decision?.selected_option_ids ?? []);
  const isDeferred = view.status === 'deferred' || view.decision?.resolution_type === 'defer';

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <div className="bg-[var(--bg-raised)] border-b border-[var(--rule)] print:bg-[var(--bg-raised)] print:border-[var(--rule)]">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-start justify-between gap-4">
            <div>
          <p className="text-xs text-[var(--text-ter)] uppercase tracking-wide mb-1 print:text-[var(--text-ter)]">Decision Record · priorities.ai</p>

          {/* Classification badges */}
          {(view.decision_type || view.decision_scope) && (
            <div className="flex flex-wrap items-center gap-1.5 mb-2">
              {view.decision_type && (() => {
                const c = DECISION_TYPE_COLORS[view.decision_type];
                return (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${c.bg} ${c.text} ${c.border} print:border-[var(--rule)] print:text-[var(--text-sec)]`}>
                    {DECISION_TYPE_LABELS[view.decision_type]}
                  </span>
                );
              })()}
              {view.decision_scope && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs border border-[var(--rule)] bg-[var(--surface)] text-[var(--text-ter)] print:border-[var(--rule)] print:text-[var(--text-sec)]">
                  {DECISION_SCOPE_LABELS[view.decision_scope]}{view.scope_label ? ` · ${view.scope_label}` : ''}
                </span>
              )}
            </div>
          )}

          <h1 className="text-2xl font-semibold text-[var(--text-pri)] print:text-[var(--text-pri)]">{view.title}</h1>
          {view.context && <p className="text-sm text-[var(--text-sec)] mt-2 max-w-2xl print:text-[var(--text-sec)]">{view.context}</p>}

          {/* Tags */}
          {view.tags && view.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {view.tags.map((tag) => (
                <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-[var(--bg)] border border-[var(--rule)] text-[var(--text-dim)] print:border-[var(--rule)] print:text-[var(--text-ter)]">
                  #{tag}
                </span>
              ))}
            </div>
          )}
            </div>
            <button
              onClick={() => window.print()}
              className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] border border-[var(--rule)] rounded px-2.5 py-1.5 transition-colors print:hidden"
            >
              <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
              Print
            </button>
          </div>
          <dl className="mt-4 flex flex-wrap gap-6 text-sm">
            {!view.decision && <Field label="Decision deadline" value={fmt(view.deadline ?? undefined)} />}
            <Field label="Decision authority" value={view.decision_authority} />
            {view.decision?.decided_at && (
              <Field
                label={isDeferred ? 'Deferred' : 'Decided'}
                value={new Date(view.decision.decided_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              />
            )}
          </dl>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-10 print:space-y-8">
        {view.decision && (
          <section className="border border-[var(--green)]/20 bg-[var(--green)]/5 p-6">
            <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-3">
              {isDeferred
                ? `Deferred${view.decision.revisit_when ? ` until ${view.decision.revisit_when}` : ''}`
                : view.decision.resolution_type
                  ? `Decision on record · ${BRIEF_RESOLUTION_LABELS[view.decision.resolution_type]}`
                  : 'Decision on record'}
            </p>
            <div className="space-y-4">
              {view.options.filter((o) => selectedIds.has(o.id)).map((o) => (
                <div key={o.id}>
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircleIcon className="w-4 h-4 text-[var(--green)]" />
                    <span className="font-medium text-[var(--text-pri)]">{o.name}</span>
                    <span className="text-xs bg-[var(--green)]/10 text-[var(--green)] dark:text-[var(--text-ter)] px-2 py-0.5 rounded">Selected</span>
                  </div>
                </div>
              ))}
            </div>

            {/* The RPC returns BriefDecision columns; this page previously read
                `rationale` / `option_rationales`, which do not exist, so the
                public record never showed why anything was chosen. */}
            {view.decision.selected_rationale && (
              <div className="mt-4 pt-4 border-t border-[var(--green)]/20">
                <p className="text-xs text-[var(--text-ter)] uppercase tracking-wide mb-1">
                  {isDeferred ? 'Why it was deferred' : view.decision.resolution_type === 'reject_all' ? 'Why every option was rejected' : 'Why this was chosen'}
                </p>
                <p className="text-sm text-[var(--text-sec)] whitespace-pre-line">{view.decision.selected_rationale}</p>
              </div>
            )}

            {view.decision.rejected_rationale && (
              <div className="mt-4 pt-4 border-t border-[var(--green)]/20">
                <p className="text-xs text-[var(--text-ter)] uppercase tracking-wide mb-1">Why the others were not chosen</p>
                <p className="text-sm text-[var(--text-sec)] whitespace-pre-line">{view.decision.rejected_rationale}</p>
              </div>
            )}

            {view.decision.conditions && (
              <div className="mt-4 pt-4 border-t border-[var(--green)]/20">
                <p className="text-xs text-[var(--text-ter)] uppercase tracking-wide mb-1">Conditions</p>
                <p className="text-sm text-[var(--text-sec)] whitespace-pre-line">{view.decision.conditions}</p>
              </div>
            )}

            {view.decision.signal_for_change && (
              <div className="mt-4 pt-4 border-t border-[var(--green)]/20">
                <p className="text-xs text-[var(--text-ter)] uppercase tracking-wide mb-1">What would make us look at this again</p>
                <p className="text-sm text-[var(--text-sec)] whitespace-pre-line">{view.decision.signal_for_change}</p>
              </div>
            )}
          </section>
        )}

        <section>
          <h2 className="text-sm font-medium text-[var(--text-ter)] uppercase tracking-wide mb-4">{view.decision ? 'Options evaluated' : 'Options under consideration'}</h2>
          <div className="space-y-6">
            {view.options.map((o) => {
              const selected = selectedIds.has(o.id);
              const proposal = view.proposals.find((p) => p.option_id === o.id);
              return (
                <div key={o.id} className={`border  p-5 ${selected ? 'border-[var(--green)]/30 bg-[var(--green)]/5' : 'border-[var(--rule)] bg-[var(--surface)]'}`}>
                  <div className="flex items-start gap-3 mb-4">
                    {/* Before a decision exists every option is still in play — a red X here read as "everything was rejected". */}
                    {!view.decision
                      ? <span aria-hidden="true" className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><span className="w-2.5 h-2.5 rounded-full bg-[var(--text-dim)]" /></span>
                      : selected
                        ? <CheckCircleIcon className="w-5 h-5 text-[var(--green)] flex-shrink-0 mt-0.5" />
                        : <XCircleIcon className="w-5 h-5 text-[var(--text-dim)] flex-shrink-0 mt-0.5" />
                    }
                    <div>
                      <span className="font-semibold text-[var(--text-pri)]">{o.name}</span>
                      {o.owner_name && <span className="text-sm text-[var(--text-ter)] ml-2">— {o.owner_name}</span>}
                    </div>
                  </div>

                  {proposal ? (
                    <dl className="space-y-3 ml-8">
                      {proposal.rationale && (
                        <div>
                          <dt className="text-xs text-[var(--text-ter)] mb-0.5">Rationale</dt>
                          <dd className="text-sm text-[var(--text-sec)]">{proposal.rationale}</dd>
                        </div>
                      )}
                      {proposal.expected_outcome && (
                        <div>
                          <dt className="text-xs text-[var(--text-ter)] mb-0.5">Expected outcome</dt>
                          <dd className="text-sm text-[var(--text-sec)]">{proposal.expected_outcome}</dd>
                        </div>
                      )}
                      {proposal.investment_summary && (
                        <div>
                          <dt className="text-xs text-[var(--text-ter)] mb-0.5">Investment</dt>
                          <dd className="text-sm text-[var(--text-sec)]">{proposal.investment_summary}</dd>
                        </div>
                      )}
                      {proposal.assumptions?.length > 0 && (
                        <div>
                          <dt className="text-xs text-[var(--text-ter)] mb-1">Key assumptions</dt>
                          <dd className="space-y-0.5">
                            {proposal.assumptions.map((a, i) => (
                              <p key={i} className="text-sm text-[var(--text-sec)] flex gap-2">
                                <span className="text-[var(--text-dim)]">·</span>{a}
                              </p>
                            ))}
                          </dd>
                        </div>
                      )}
                      {proposal.top_risk && (
                        <div>
                          <dt className="text-xs text-[var(--text-ter)] mb-0.5">Top risk</dt>
                          <dd className="text-sm text-[var(--text-sec)]">{proposal.top_risk}</dd>
                        </div>
                      )}
                    </dl>
                  ) : (
                    <p className="text-sm text-[var(--text-dim)] ml-8 italic">No proposal submitted</p>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {view.criteria?.length > 0 && (
          <section>
            <h2 className="text-sm font-medium text-[var(--text-ter)] uppercase tracking-wide mb-3">Evaluation criteria</h2>
            <div className="space-y-2">
              {view.criteria.map((c, i) => (
                <div key={c.id} className="flex items-start gap-3 text-sm">
                  <span className="text-[var(--text-dim)] font-mono text-xs mt-0.5 w-5">{i + 1}</span>
                  <div>
                    <span className="font-medium text-[var(--text-sec)]">{c.name}</span>
                    {c.description && <span className="text-[var(--text-ter)]"> — {c.description}</span>}
                    <span className="text-[var(--text-dim)]"> · weight {c.weight}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section>
          <h2 className="text-sm font-medium text-[var(--text-ter)] uppercase tracking-wide mb-3">Decision provenance</h2>
          <div className="space-y-1.5 text-sm">
            {view.decision_type && (
              <div className="flex gap-3">
                <span className="text-[var(--text-dim)] w-32 flex-shrink-0">Decision type</span>
                <span className="text-[var(--text-sec)]">{DECISION_TYPE_LABELS[view.decision_type]}</span>
              </div>
            )}
            {view.decision_scope && (
              <div className="flex gap-3">
                <span className="text-[var(--text-dim)] w-32 flex-shrink-0">Scope</span>
                <span className="text-[var(--text-sec)]">{DECISION_SCOPE_LABELS[view.decision_scope]}{view.scope_label ? ` — ${view.scope_label}` : ''}</span>
              </div>
            )}
            <div className="flex gap-3">
              <span className="text-[var(--text-dim)] w-32 flex-shrink-0">Decision authority</span>
              <span className="text-[var(--text-sec)]">{view.decision_authority ?? '—'}</span>
            </div>
            {view.decision?.decided_at && (
              <div className="flex gap-3">
                <span className="text-[var(--text-dim)] w-32 flex-shrink-0">Decided on</span>
                <span className="text-[var(--text-sec)]">{new Date(view.decision.decided_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
            )}
            <div className="flex gap-3">
              <span className="text-[var(--text-dim)] w-32 flex-shrink-0">Options evaluated</span>
              <span className="text-[var(--text-sec)]">{view.options.length}</span>
            </div>
            <div className="flex gap-3">
              <span className="text-[var(--text-dim)] w-32 flex-shrink-0">Proposals received</span>
              <span className="text-[var(--text-sec)]">{view.proposals.length}</span>
            </div>
            {view.criteria?.length > 0 && (
              <div className="flex gap-3">
                <span className="text-[var(--text-dim)] w-32 flex-shrink-0">Criteria defined</span>
                <span className="text-[var(--text-sec)]">{view.criteria.length}</span>
              </div>
            )}
          </div>
        </section>

        <footer className="pt-6 border-t border-[var(--rule)] text-xs text-[var(--text-dim)]">
          Decision Record — priorities.ai · The Frame was locked before proposals were collected.
        </footer>
      </div>
    </div>
  );
}
